/**
 * API Endpoints Constants
 *
 * Centralized definition of all API endpoints to prevent typos and improve maintainability.
 * Following Martin Fowler's "Replace Magic String with Named Constant" refactoring technique.
 *
 * @example
 * ```typescript
 * // Before: fetch(`/admin/orders/${id}/status`)
 * // After:  fetch(API_ENDPOINTS.orders.status(id))
 * ```
 */
/**
 * Order-related API endpoints
 */
export const ORDER_ENDPOINTS = {
    /**
     * Canonical endpoints - simple state changes without side effects
     */
    canonical: {
        /** PUT /admin/orders/:id/status - Change order status */
        status: (orderId) => `/admin/orders/${orderId}/status`,
        /** POST /admin/orders/:id/assign - Assign florist or courier */
        assign: (orderId) => `/admin/orders/${orderId}/assign`,
        /** POST /admin/orders/bulk-assign - Bulk assign florist or courier */
        bulkAssign: '/admin/orders/bulk-assign',
        /** DELETE /admin/orders/:id - Cancel order */
        cancel: (orderId) => `/admin/orders/${orderId}`,
    },
    /**
     * Command endpoints - atomic operations with side effects
     */
    command: {
        /** POST /admin/orders/:id/start-florist - Florist starts work (confirmed → preparing) */
        startFlorist: (orderId) => `/admin/orders/${orderId}/start-florist`,
        /** POST /admin/orders/:id/unassign-florist - Remove florist assignment (confirmed → pending) */
        unassignFlorist: (orderId) => `/admin/orders/${orderId}/unassign-florist`,
        /** POST /admin/orders/:id/reschedule - Reschedule delivery date/time */
        reschedule: (orderId) => `/admin/orders/${orderId}/reschedule`,
        /** POST /admin/orders/:id/update-eta - Update estimated delivery time */
        updateEta: (orderId) => `/admin/orders/${orderId}/update-eta`,
        /** POST /admin/orders/:id/sync-payment - Sync payment status from BirPay */
        syncPayment: (orderId) => `/admin/orders/${orderId}/sync-payment`,
        /** POST /admin/orders/:id/recover-payment - Recover cancelled order via BirPay sync */
        recoverPayment: (orderId) => `/admin/orders/${orderId}/recover-payment`,
        /** POST /admin/orders/:id/cancel-and-refund - Cancel order with automatic payment handling (Process Manager) */
        cancelAndRefund: (orderId) => `/admin/orders/${orderId}/cancel-and-refund`,
        /** POST /admin/orders/:id/cash-payment/confirm - Florist/admin confirms cash received from customer */
        confirmCashPayment: (orderId) => `/admin/orders/${orderId}/cash-payment/confirm`,
        /** POST /admin/orders/:id/cash-payment/cancel - Admin-only undo of a recorded cash payment */
        cancelCashPayment: (orderId) => `/admin/orders/${orderId}/cash-payment/cancel`,
    },
    /**
     * Query endpoints
     */
    query: {
        /** GET /admin/orders - List orders with filters */
        list: '/admin/orders',
        /** GET /admin/orders/:id - Get single order details */
        detail: (orderId) => `/admin/orders/${orderId}`,
        /** GET /orders/:id/tracking - Get order tracking info */
        tracking: (orderId) => `/orders/${orderId}/tracking`,
    },
    /**
     * Customer-facing endpoints
     */
    customer: {
        /** POST /orders/:id/cancel - Cancel order (customer can cancel their own) */
        cancel: (orderId) => `/orders/${orderId}/cancel`,
    },
};
/**
 * Florist-specific API endpoints
 */
export const FLORIST_ENDPOINTS = {
    /** POST /orders/:id/accept-florist - Florist accepts assigned order */
    acceptJob: (orderId) => `/orders/${orderId}/accept-florist`,
    /** POST /orders/:id/mark-prepared - Mark bouquet as ready with photo */
    markPrepared: (orderId) => `/orders/${orderId}/mark-prepared`,
    /** POST /admin/florist/assign - Assign florist to order (manager only) */
    assign: '/admin/florist/assign',
    /** POST /admin/florist/decline - Decline order */
    decline: '/admin/florist/decline',
    /** GET /admin/florist/pending-orders - Get unassigned orders */
    pendingOrders: '/admin/florist/pending-orders',
    /** GET /admin/florist/confirmations - Get florist confirmations */
    confirmations: '/admin/florist/confirmations',
};
/**
 * Courier-specific API endpoints
 *
 * Note: courier status transitions (start/complete delivery) go through
 * PUT /admin/orders/:id/status per V3 contract.
 */
export const COURIER_ENDPOINTS = {
    /** POST /courier-gps/location - Update courier GPS location */
    updateLocation: '/courier-gps/location',
};
/**
 * Authentication endpoints
 */
export const AUTH_ENDPOINTS = {
    /** POST /auth/login - User login */
    login: '/auth/login',
    /** GET /auth/me - Get current user */
    me: '/auth/me',
    /** POST /auth/refresh - Refresh auth token */
    refresh: '/auth/refresh',
    /** POST /auth/logout - User logout */
    logout: '/auth/logout',
};
/**
 * Media upload endpoints
 */
export const MEDIA_ENDPOINTS = {
    /** POST /media/upload-url - Get presigned upload URL */
    uploadUrl: '/media/upload-url',
    /** POST /media/upload-proxy - Upload via proxy (mobile) */
    uploadProxy: '/media/upload-proxy',
};
/**
 * User management endpoints
 */
export const USER_ENDPOINTS = {
    /** GET /admin/users - List users */
    list: '/admin/users',
    /** POST /admin/users - Create user */
    create: '/admin/users',
    /** PUT /admin/users/:id - Update user */
    update: (userId) => `/admin/users/${userId}`,
    /** DELETE /admin/users/:id - Deactivate user */
    delete: (userId) => `/admin/users/${userId}`,
    /** PUT /admin/users/:id/activate - Reactivate user */
    activate: (userId) => `/admin/users/${userId}/activate`,
};
/**
 * Catalog management endpoints
 */
export const CATALOG_ENDPOINTS = {
    flowers: {
        list: '/admin/flowers',
        detail: (flowerId) => `/admin/flowers/${flowerId}`,
        create: '/admin/flowers',
        update: (flowerId) => `/admin/flowers/${flowerId}`,
        delete: (flowerId) => `/admin/flowers/${flowerId}`,
        availability: (flowerId) => `/admin/flowers/${flowerId}/availability`,
        bulk: '/admin/flowers/bulk',
    },
    bouquets: {
        list: '/admin/bouquets',
        detail: (bouquetId) => `/admin/bouquets/${bouquetId}`,
        create: '/admin/bouquets',
        update: (bouquetId) => `/admin/bouquets/${bouquetId}`,
        delete: (bouquetId) => `/admin/bouquets/${bouquetId}`,
    },
    categories: {
        list: '/admin/categories',
        detail: (categoryId) => `/admin/categories/${categoryId}`,
        create: '/admin/categories',
        update: (categoryId) => `/admin/categories/${categoryId}`,
        delete: (categoryId) => `/admin/categories/${categoryId}`,
        forceDelete: (categoryId) => `/admin/categories/${categoryId}/force`,
        reorder: '/admin/categories/reorder',
    },
};
/**
 * Chat/Support endpoints
 */
export const CHAT_ENDPOINTS = {
    list: '/admin/chats',
    detail: (chatId) => `/admin/chats/${chatId}`,
    messages: (chatId) => `/admin/chats/${chatId}/messages`,
    sendMessage: (chatId) => `/admin/chats/${chatId}/messages`,
    updateStatus: (chatId) => `/admin/chats/${chatId}/status`,
    markAsRead: (chatId) => `/admin/chats/${chatId}/read`,
    assign: (chatId) => `/admin/chats/${chatId}/assign`,
    close: (chatId) => `/admin/chats/${chatId}/close`,
    reopen: (chatId) => `/admin/chats/${chatId}/reopen`,
    priority: (chatId) => `/admin/chats/${chatId}/priority`,
    category: (chatId) => `/admin/chats/${chatId}/category`,
};
/**
 * Quick replies endpoints
 */
export const QUICK_REPLY_ENDPOINTS = {
    list: '/admin/quick-replies',
    create: '/admin/quick-replies',
    update: (replyId) => `/admin/quick-replies/${replyId}`,
    delete: (replyId) => `/admin/quick-replies/${replyId}`,
    reorder: '/admin/quick-replies/reorder',
};
/**
 * Greeting cards endpoints
 */
export const GREETING_CARD_ENDPOINTS = {
    byOrder: (orderId) => `/greeting-cards/order/${orderId}`,
};
/**
 * Site settings endpoints
 */
export const SETTINGS_ENDPOINTS = {
    hero: {
        get: '/settings/hero',
        update: '/settings/admin/hero',
    },
};
/**
 * Statistics endpoints
 */
export const STATS_ENDPOINTS = {
    general: '/admin/stats',
    navigation: '/admin/navigation-counts',
    tickets: '/admin/chats/tickets/statistics',
};
/**
 * Behavior Analytics endpoints
 * Note: Paths without /api prefix - apiClient adds baseUrl with /api
 */
export const ANALYTICS_ENDPOINTS = {
    /** POST /analytics/track - Public event tracking (rate limited) */
    track: '/analytics/track',
    /** Admin endpoints */
    admin: {
        /** GET /admin/analytics/summary - Get analytics summary */
        summary: '/admin/analytics/summary',
        /** GET /admin/analytics/timeseries - Get time series data */
        timeseries: '/admin/analytics/timeseries',
        /** GET /admin/analytics/top-products - Get top viewed/purchased products */
        topProducts: '/admin/analytics/top-products',
        /** GET /admin/analytics/top-searches - Get top search queries */
        topSearches: '/admin/analytics/top-searches',
        /** GET /admin/analytics/attention - Get attention metrics (scroll, blocks) */
        attention: '/admin/analytics/attention',
        /** GET /admin/analytics/funnel - Get conversion funnel data */
        funnel: '/admin/analytics/funnel',
        /** GET /admin/analytics/export - Export data as CSV */
        export: '/admin/analytics/export',
        /** GET /admin/analytics/page-metrics - Page-level visitor metrics (Phase 1) */
        pageMetrics: '/admin/analytics/page-metrics',
        /** GET /admin/analytics/page-flow - Page-to-page transition flow (Phase 1) */
        pageFlow: '/admin/analytics/page-flow',
        /** GET /admin/analytics/checkout-funnel - Step-level checkout funnel (Phase 2) */
        checkoutFunnel: '/admin/analytics/checkout-funnel',
        /** GET /admin/analytics/friction-points - Friction/error aggregation (Phase 2) */
        frictionPoints: '/admin/analytics/friction-points',
        /** GET /admin/analytics/attention-conversion - Attention-conversion correlation (Phase 3) */
        attentionConversion: '/admin/analytics/attention-conversion',
        /** GET /admin/analytics/interactions - Element interaction metrics (Phase 4) */
        interactions: '/admin/analytics/interactions',
        /** GET /admin/analytics/behavior-summary - Behavior-centric summary KPIs (Phase 5) */
        behaviorSummary: '/admin/analytics/behavior-summary',
    },
};
/**
 * Notifications endpoints
 */
export const NOTIFICATION_ENDPOINTS = {
    list: '/admin/notifications',
    markAsRead: (notificationId) => `/admin/notifications/${notificationId}/read`,
    markAllAsRead: '/admin/notifications/read-all',
    delete: (notificationId) => `/admin/notifications/${notificationId}`,
};
/**
 * Business Reports endpoints (dashboard KPIs, revenue, operations)
 */
export const REPORT_ENDPOINTS = {
    /** GET /admin/reports/revenue - Revenue KPIs + time series */
    revenue: '/admin/reports/revenue',
    /** GET /admin/reports/operations - Fulfillment & delivery metrics */
    operations: '/admin/reports/operations',
    /** GET /admin/reports/products - Top products by revenue */
    products: '/admin/reports/products',
    /** GET /admin/reports/customers - New vs repeat customers */
    customers: '/admin/reports/customers',
    /** GET /admin/reports/support - Support SLA compliance */
    support: '/admin/reports/support',
    /** GET /admin/reports/alerts - Real-time exception alerts */
    alerts: '/admin/reports/alerts',
    /** GET /admin/reports/payments - BirBank payment & commission report */
    payments: '/admin/reports/payments',
};
/**
 * Combined API endpoints export
 * Provides easy access to all endpoints from a single import
 *
 * @example
 * ```typescript
 * import { API_ENDPOINTS } from '@agciceyim/shared'
 *
 * fetch(API_ENDPOINTS.orders.canonical.status('order-123'))
 * fetch(API_ENDPOINTS.florist.acceptJob('order-123'))
 * ```
 */
export const API_ENDPOINTS = {
    orders: ORDER_ENDPOINTS,
    florist: FLORIST_ENDPOINTS,
    courier: COURIER_ENDPOINTS,
    auth: AUTH_ENDPOINTS,
    media: MEDIA_ENDPOINTS,
    users: USER_ENDPOINTS,
    catalog: CATALOG_ENDPOINTS,
    chats: CHAT_ENDPOINTS,
    quickReplies: QUICK_REPLY_ENDPOINTS,
    greetingCards: GREETING_CARD_ENDPOINTS,
    settings: SETTINGS_ENDPOINTS,
    stats: STATS_ENDPOINTS,
    notifications: NOTIFICATION_ENDPOINTS,
    analytics: ANALYTICS_ENDPOINTS,
    reports: REPORT_ENDPOINTS,
};
// ... (TypeScript will infer all possible types)
